import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Loader2, Check, X, Clock, ShoppingCart, Trash2, ArrowUp, Bus } from 'lucide-react';
import { getBaseUrl, BRAND_NAME } from '@/config/appConfig';
import { getEmbedTargetOrigin } from '../utils/embedOrigin';

const API_URL = getBaseUrl();

// Embeddable Checkout Widget - For seat selection in ticketing flows
export default function EmbedCheckout() {
  const { layoutId } = useParams();
  const [searchParams] = useSearchParams();
  const apiKey = searchParams.get('key');
  const maxSeats = parseInt(searchParams.get('maxSeats') || '10');
  const holdDuration = parseInt(searchParams.get('holdDuration') || '15');
  
  const [branding, setBranding] = useState(null);
  const [layout, setLayout] = useState(null);
  const [seats, setSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [holdToken, setHoldToken] = useState(null);
  const [holdExpiry, setHoldExpiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserving, setReserving] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isBusLayout, setIsBusLayout] = useState(false);
  const [busLayoutData, setBusLayoutData] = useState(null);

  // Load branding
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/branding?key=${apiKey}`);
        if (response.ok) {
          const data = await response.json();
          setBranding(data.branding);
        }
      } catch (err) {
        console.warn('Could not load branding');
      }
    };
    if (apiKey) loadBranding();
  }, [apiKey]);

  // Load layout and seats
  useEffect(() => {
    const loadLayout = async () => {
      if (!layoutId || !apiKey) {
        setError('Layout ID and API key are required');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/public/layouts/${layoutId}`, {
          headers: { 'X-API-Key': apiKey }
        });

        if (!response.ok) {
          throw new Error('Failed to load layout');
        }

        const data = await response.json();
        setLayout(data.layout);
        setSeats(data.seats || []);
        
        // Detect if this is a bus layout
        if (data.layout?.canvas_objects?.some(obj => obj.type === 'bus_layout')) {
          setIsBusLayout(true);
          // Extract bus layout object for rendering
          const busObj = data.layout.canvas_objects.find(obj => obj.type === 'bus_layout');
          setBusLayoutData(busObj);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadLayout();
  }, [layoutId, apiKey]);

  // Post message to parent
  const postMessage = useCallback((type, data = {}) => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: `seatpilot:${type}`, data }, getEmbedTargetOrigin());
    }
  }, []);

  // Timer for hold expiry
  useEffect(() => {
    if (!holdExpiry) {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(holdExpiry);
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setTimeLeft(diff);

      if (diff === 0) {
        // Hold expired
        setHoldToken(null);
        setHoldExpiry(null);
        setSelectedSeats([]);
        refreshSeats();
        postMessage('seats_released', { reason: 'expired' });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [holdExpiry, postMessage]);
// Refresh seats
  const refreshSeats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/layouts/${layoutId}`, {
        headers: { 'X-API-Key': apiKey }
      });
      if (response.ok) {
        const data = await response.json();
        setSeats(data.seats || []);
      }
    } catch (err) {
      console.error('Failed to refresh seats');
    }
  };

  // Handle seat click
  const handleSeatClick = (seat) => {
    if (holdToken) {
      // Can't change selection while holding
      return;
    }

    if (seat.status !== 'available') {
      return;
    }

    const isSelected = selectedSeats.some(s => s.seat_ref === seat.seat_ref);
    
    if (isSelected) {
      // Deselect
      const newSelected = selectedSeats.filter(s => s.seat_ref !== seat.seat_ref);
      setSelectedSeats(newSelected);
      postMessage('seat_deselected', { seat });
    } else {
      // Select (if under max)
      if (selectedSeats.length >= maxSeats) {
        setError(`Maximum ${maxSeats} seats allowed`);
        setTimeout(() => setError(null), 3000);
        return;
      }
      const newSelected = [...selectedSeats, seat];
      setSelectedSeats(newSelected);
      postMessage('seat_selected', { seat });
    }
  };

  // Reserve selected seats
  const reserveSeats = async () => {
    if (selectedSeats.length === 0) return;

    setReserving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/public/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          layout_id: layoutId,
          seats: selectedSeats.map(s => ({ seat_ref: s.seat_ref })),
          hold_duration_minutes: holdDuration
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reserve seats');
      }

      const data = await response.json();
      setHoldToken(data.hold_token);
      setHoldExpiry(data.expires_at);
      
      postMessage('seats_reserved', {
        hold_token: data.hold_token,
        seats: selectedSeats,
        expires_at: data.expires_at
      });

      await refreshSeats();
    } catch (err) {
      setError(err.message);
      postMessage('error', { message: err.message });
    } finally {
      setReserving(false);
    }
  };

  // Release reservation
  const releaseReservation = async () => {
    if (!holdToken) return;

    try {
      await fetch(`${API_URL}/api/public/release`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({ hold_token: holdToken })
      });

      setHoldToken(null);
      setHoldExpiry(null);
      setSelectedSeats([]);
      postMessage('seats_released', { reason: 'user' });
      await refreshSeats();
    } catch (err) {
      setError(err.message);
    }
  };

  // Confirm reservation
  const confirmReservation = async () => {
    if (!holdToken) return;

    try {
      const response = await fetch(`${API_URL}/api/public/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({ hold_token: holdToken })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to confirm reservation');
      }

      const data = await response.json();
      postMessage('seats_confirmed', {
        seats: selectedSeats,
        seats_confirmed: data.seats_confirmed
      });

      setHoldToken(null);
      setHoldExpiry(null);
      // Don't clear selected seats - parent may want to show them
    } catch (err) {
      setError(err.message);
      postMessage('error', { message: err.message });
    }
  };

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get colors from branding
  const colors = branding?.colors || {};
  const primaryColor = colors.primary || '#6366f1';
  const seatAvailable = colors.seat_available || '#10b981';
  const seatSelected = colors.seat_selected || '#6366f1';
  const seatHeld = colors.seat_held || '#f59e0b';
  const seatBooked = colors.seat_booked || '#9ca3af';

  // Get seat status color
  const getSeatColor = (seat) => {
    if (selectedSeats.some(s => s.seat_ref === seat.seat_ref)) {
      return seatSelected;
    }
    switch (seat.status) {
      case 'available': return seatAvailable;
      case 'held': return seatHeld;
      case 'booked': return seatBooked;
      default: return '#d1d5db';
    }
  };

  // Group seats by row
  const seatsByRow = seats.reduce((acc, seat) => {
    const parts = seat.seat_ref.split('-');
    const rowLabel = parts[0] || 'MISC';
    if (!acc[rowLabel]) acc[rowLabel] = [];
    acc[rowLabel].push(seat);
    return acc;
  }, {});

  // Sort rows
  const sortedRows = Object.keys(seatsByRow).sort();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ backgroundColor: colors.background || '#ffffff' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-4"
      style={{ 
        fontFamily: branding?.fonts?.primary || 'Inter, system-ui, sans-serif',
        backgroundColor: colors.background || '#ffffff',
        color: colors.text || '#1f2937'
      }}
    >
      {/* Header */}
      {layout && (
        <div className="mb-4">
          <h2 className="text-xl font-semibold">{layout.name}</h2>
          {layout.venue_name && (
            <p className="text-sm" style={{ color: colors.text_secondary || '#6b7280' }}>
              {layout.venue_name}
            </p>
          )}
          {layout.event_name && (
            <p className="text-sm font-medium" style={{ color: primaryColor }}>
              {layout.event_name}
              {layout.event_date && ` • ${layout.event_date}`}
            </p>
          )}
        </div>
      )}

      {error && (
        <Alert className="mb-4" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Timer bar */}
      {holdToken && timeLeft !== null && (
        <div 
          className="mb-4 p-3 rounded-lg flex items-center justify-between"
          style={{ backgroundColor: timeLeft < 60 ? '#fef3c7' : '#e0f2fe' }}
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" style={{ color: timeLeft < 60 ? '#d97706' : '#0284c7' }} />
            <span className="text-sm font-medium" style={{ color: timeLeft < 60 ? '#92400e' : '#075985' }}>
              Seats held for {formatTime(timeLeft)}
            </span>
          </div>
          <Button size="sm" variant="ghost" onClick={releaseReservation}>
            <X className="h-4 w-4 mr-1" />
            Release
          </Button>
        </div>
      )}

      {/* Screen indicator */}
      {layout?.show_screen && (
        <div 
          className="mb-6 py-2 rounded text-center text-sm font-medium"
          style={{ backgroundColor: colors.surface || '#f3f4f6', color: colors.text_secondary || '#6b7280' }}
        >
          {layout.screen_label || 'SCREEN'}
        </div>
      )}

      {/* Seat map */}
      <div className="overflow-x-auto mb-6">
        {isBusLayout && busLayoutData ? (
          /* Bus Layout - Flight Style Rendering */
          <div 
            className="relative mx-auto p-4 rounded-2xl"
            style={{ 
              backgroundColor: '#f8fafc',
              border: '3px solid #cbd5e1',
              maxWidth: '350px',
              borderRadius: '30px 30px 15px 15px'
            }}
          >
            {/* Driver area */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-dashed border-slate-300">
              <div className="flex flex-col items-center bg-blue-100 px-2 py-1 rounded-lg">
                <ArrowUp size={14} className="text-blue-600" />
                <span className="text-[8px] text-blue-600 font-bold">FRONT</span>
              </div>
              <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center">
                <Bus size={20} className="text-white" />
              </div>
              <div className="w-8 h-8 border-3 border-slate-400 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-slate-400 rounded-full" />
              </div>
            </div>
            
            {/* Seats grid - Flight style */}
            <div className="space-y-2">
              {/* Group seats by row */}
              {Array.from({ length: busLayoutData.properties?.row_count || 9 }, (_, rowIdx) => {
                const rowSeats = seats.filter(seat => {
                  const rowMatch = seat.seat_ref.match(/^(\d+)/);
                  return rowMatch && parseInt(rowMatch[1]) === rowIdx + 1;
                });
                
                const leftSeats = busLayoutData.properties?.left_seats || 2;
                const rightSeats = busLayoutData.properties?.right_seats || 2;
                
                return (
                  <div key={rowIdx} className="flex items-center justify-center gap-1">
                    {/* Left side seats */}
                    {Array.from({ length: leftSeats }, (_, seatIdx) => {
                      const seatLetter = String.fromCharCode(65 + seatIdx);
                      const seatRef = `${rowIdx + 1}${seatLetter}`;
                      const seat = rowSeats.find(s => s.seat_ref === seatRef);
                      const isSelected = selectedSeats.some(s => s.seat_ref === seatRef);
                      const isClickable = seat?.status === 'available' && !holdToken;
                      
                      let bgColor = seatAvailable;
                      if (!seat || seat.status === 'booked') bgColor = seatBooked;
                      else if (seat.status === 'held') bgColor = seatHeld;
                      else if (isSelected) bgColor = seatSelected;
                      
                      return (
                        <button
                          key={seatRef}
                          className={`
                            w-10 h-10 rounded-t-lg rounded-b-sm text-xs font-bold transition-all
                            flex items-center justify-center
                            ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed'}
                            ${isSelected ? 'ring-2 ring-offset-1 ring-blue-500' : ''}
                          `}
                          style={{ 
                            backgroundColor: bgColor,
                            color: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                          onClick={() => seat && handleSeatClick(seat)}
                          disabled={!isClickable}
                          title={`${seatRef} - ${seat?.status || 'unavailable'}`}
                        >
                          {seatRef}
                        </button>
                      );
                    })}
                    
                    {/* Aisle */}
                    <div className="w-6 h-10 flex items-center justify-center">
                      <div className="w-1 h-full bg-slate-200 rounded-full" />
                    </div>
                    
                    {/* Right side seats */}
                    {Array.from({ length: rightSeats }, (_, seatIdx) => {
                      const seatLetter = String.fromCharCode(65 + leftSeats + seatIdx);
                      const seatRef = `${rowIdx + 1}${seatLetter}`;
                      const seat = rowSeats.find(s => s.seat_ref === seatRef);
                      const isSelected = selectedSeats.some(s => s.seat_ref === seatRef);
                      const isClickable = seat?.status === 'available' && !holdToken;
                      
                      let bgColor = seatAvailable;
                      if (!seat || seat.status === 'booked') bgColor = seatBooked;
                      else if (seat.status === 'held') bgColor = seatHeld;
                      else if (isSelected) bgColor = seatSelected;
                      
                      return (
                        <button
                          key={seatRef}
                          className={`
                            w-10 h-10 rounded-t-lg rounded-b-sm text-xs font-bold transition-all
                            flex items-center justify-center
                            ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed'}
                            ${isSelected ? 'ring-2 ring-offset-1 ring-blue-500' : ''}
                          `}
                          style={{ 
                            backgroundColor: bgColor,
                            color: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                          onClick={() => seat && handleSeatClick(seat)}
                          disabled={!isClickable}
                          title={`${seatRef} - ${seat?.status || 'unavailable'}`}
                        >
                          {seatRef}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            
            {/* Back of bus indicator */}
            <div className="mt-4 pt-2 border-t-2 border-dashed border-slate-300 text-center">
              <span className="text-xs text-slate-500 font-medium">BACK</span>
            </div>
          </div>
        ) : (
          /* Standard seat map rendering */
          <div className="inline-block min-w-full">
          {sortedRows.map(rowLabel => (
            <div key={rowLabel} className="flex items-center gap-2 mb-2">
              <div className="w-8 text-xs font-medium text-right" style={{ color: colors.text_secondary || '#6b7280' }}>
                {rowLabel}
              </div>
              <div className="flex gap-1 flex-wrap">
                {seatsByRow[rowLabel]
                  .sort((a, b) => {
                    const numA = parseInt(a.seat_ref.split('-')[1]) || 0;
                    const numB = parseInt(b.seat_ref.split('-')[1]) || 0;
                    return numA - numB;
                  })
                  .map(seat => {
                    const isSelected = selectedSeats.some(s => s.seat_ref === seat.seat_ref);
                    const isClickable = seat.status === 'available' && !holdToken;
                    
                    return (
                      <button
                        key={seat.id}
                        className={`
                          w-8 h-8 rounded text-xs font-medium transition-all
                          flex items-center justify-center
                          ${isClickable ? 'hover:scale-110 cursor-pointer' : 'cursor-not-allowed'}
                          ${isSelected ? 'ring-2 ring-offset-1' : ''}
                        `}
                        style={{ 
                          backgroundColor: getSeatColor(seat),
                          color: seat.status === 'available' || isSelected ? '#ffffff' : '#4b5563',
                          ringColor: primaryColor
                        }}
                        onClick={() => handleSeatClick(seat)}
                        disabled={!isClickable}
                        title={`${seat.seat_ref} - ${seat.status}`}
                      >
                        {seat.seat_ref.split('-')[1]}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: seatAvailable }} />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: seatSelected }} />
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: seatHeld }} />
          <span>Held</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: seatBooked }} />
          <span>Booked</span>
        </div>
      </div>

      {/* Selection summary & actions */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Your Selection ({selectedSeats.length}/{maxSeats})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedSeats.length === 0 ? (
            <p className="text-sm" style={{ color: colors.text_secondary || '#6b7280' }}>
              Click on available seats to select them
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {selectedSeats.map(seat => (
                  <Badge 
                    key={seat.seat_ref} 
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {seat.seat_ref}
                    {!holdToken && (
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-red-500" 
                        onClick={() => handleSeatClick(seat)}
                      />
                    )}
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                {!holdToken ? (
                  <Button 
                    onClick={reserveSeats}
                    disabled={reserving || selectedSeats.length === 0}
                    style={{ backgroundColor: primaryColor }}
                    className="flex-1"
                  >
                    {reserving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Reserving...
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4 mr-2" />
                        Hold for {holdDuration} min
                      </>
                    )}
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={confirmReservation}
                      style={{ backgroundColor: colors.success || '#10b981' }}
                      className="flex-1"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Confirm Booking
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={releaseReservation}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Powered by footer */}
      {branding?.layout?.show_powered_by !== false && (
        <div className="text-center py-4 text-xs" style={{ color: colors.text_secondary || '#9ca3af' }}>
          Powered by <a href={getBaseUrl()} target="_blank" rel="noopener noreferrer" style={{ color: primaryColor }}>{BRAND_NAME}</a>
        </div>
      )}
    </div>
  );
}
